import { useTranslate, HttpError } from '@refinedev/core';
import { Edit, useForm, useSelect } from '@refinedev/antd';
import { Form, Input, Radio, Select, Space, Button, InputNumber } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import type { BannerOut, BannerUpdateIn } from '@/api/generated/schemas';
import { ModStatus } from '@/api/generated/schemas';

export const BannerEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, query } = useForm<
    BannerOut,
    HttpError,
    BannerUpdateIn
  >({
    resource: 'user_catalog.banners',
    mutationMeta: { method: 'patch' },
  });

  const record = query?.data?.data;

  const { selectProps: bannerActionSelectProps } = useSelect({
    resource: 'user_catalog.banner_actions',
    optionLabel: 'name',
    optionValue: 'id',
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
    defaultValue: record?.banner_links?.map((bl) => bl.banner_action_id),
  });

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item
          name="name"
          label={t('user_catalog.banners.fields.name')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('user_catalog.banners.fields.description')}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="mod_status"
          label={t('base.fields.mod_status')}
          rules={[{ required: true }]}
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={Object.values(ModStatus).map((value) => ({
              label: t(`base.mod_status.${value}`),
              value,
            }))}
          />
        </Form.Item>

        <Form.List name="banner_links">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {t('user_catalog.banners.banner_links.title')}
              </div>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  align="end"
                  style={{ display: 'flex', marginBottom: 8 }}
                >
                  <Form.Item {...restField} name={[name, 'id']} hidden>
                    <Input />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, 'position']}
                    label={t('user_catalog.banners.fields.position')}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber min={0} style={{ width: 100 }} />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, 'banner_action_id']}
                    label={t('user_catalog.banners.fields.banner_action')}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      {...bannerActionSelectProps}
                      showSearch
                      style={{ minWidth: 240 }}
                      placeholder={t(
                        'user_catalog.banners.fields.banner_action_placeholder'
                      )}
                      filterOption={false}
                    />
                  </Form.Item>

                  <MinusCircleOutlined
                    style={{ marginBottom: 8, color: '#ff4d4f' }}
                    onClick={() => remove(name)}
                  />
                </Space>
              ))}

              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  {t('user_catalog.banners.banner_links.add')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Edit>
  );
};
